import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getTeamStats, getSdrAppointmentsInRange, type TeamStatRow, type DayRdv } from '../../lib/refonteApi';
import { useAuth } from '../../components/AuthProvider';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';
import StatusBadge from '../../components/refonte/StatusBadge';

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mondayOf = (d: Date) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

type Col = { key: keyof TeamStatRow; label: string; grp: 'act' | 'conv'; suffix?: string; hint?: string };
const COLS: Col[] = [
  { key: 'composes', label: 'Composés', grp: 'act', hint: 'Numéros composés (Minari)' },
  { key: 'connectes', label: 'Connectés', grp: 'act', hint: 'Appels décrochés (Minari)' },
  { key: 'echanges', label: 'Conversations', grp: 'act', hint: 'Connectés > 30s (dico §2 — l’unité qui compte)' },
  { key: 'echRate', label: '%conv', grp: 'act', suffix: '%', hint: 'Conversations / connectés' },
  { key: 'perDay', label: 'Conn./jour', grp: 'act', hint: 'Connectés par jour actif' },
  { key: 'bookes', label: 'Bookés', grp: 'conv', hint: 'RDV posés' },
  { key: 'honores', label: 'Honorés', grp: 'conv', hint: 'RDV qui ont eu lieu (clique le chiffre pour voir les RDV)' },
  { key: 'convRate', label: 'éch→RDV', grp: 'conv', suffix: '%' },
  { key: 'showUp', label: 'Show-up', grp: 'conv', suffix: '%' },
];

const StatsEquipePage: React.FC = () => {
  const { profile } = useAuth();
  const meId = profile?.user_id ?? null;
  const [mode, setMode] = useState<'jour' | 'semaine' | 'mois'>('mois');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [rows, setRows] = useState<TeamStatRow[]>([]);
  const [sortKey, setSortKey] = useState<keyof TeamStatRow>('honores');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [team, setTeam] = useState<'all' | 'cold_call' | 'gtm'>('all'); // filtre Rennes (cold call) / Paris (GTM)
  const [drill, setDrill] = useState<{ name: string; loading: boolean; rows: DayRdv[] } | null>(null);

  const range = useMemo(() => {
    if (mode === 'jour') return { from: new Date(anchor), to: new Date(anchor), label: anchor.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }) };
    if (mode === 'semaine') { const s = mondayOf(anchor); const e = new Date(s); e.setDate(e.getDate() + 6); return { from: s, to: e, label: `Semaine du ${s.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}` }; }
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1); const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: s, to: e, label: anchor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) };
  }, [mode, anchor]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setRows(await getTeamStats(new Date(iso(range.from) + 'T00:00:00').toISOString(), new Date(iso(range.to) + 'T23:59:59').toISOString())); }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [range.from, range.to]);
  useEffect(() => { load(); }, [load]);

  const shift = (n: number) => { const d = new Date(anchor); if (mode === 'jour') d.setDate(d.getDate() + n); else if (mode === 'semaine') d.setDate(d.getDate() + n * 7); else d.setMonth(d.getMonth() + n); setAnchor(d); };
  const sorted = useMemo(() =>
    rows.filter(r => team === 'all' || r.type === team)
      .sort((a, b) => (Number(b[sortKey]) || 0) - (Number(a[sortKey]) || 0)),
    [rows, sortKey, team]);
  const openDrill = async (row: TeamStatRow) => {
    setDrill({ name: row.name, loading: true, rows: [] });
    try {
      const rs = await getSdrAppointmentsInRange(row.sdrId, new Date(iso(range.from) + 'T00:00:00').toISOString(), new Date(iso(range.to) + 'T23:59:59').toISOString());
      setDrill({ name: row.name, loading: false, rows: rs });
    } catch { setDrill({ name: row.name, loading: false, rows: [] }); }
  };
  const myRank = sorted.findIndex(r => r.sdrId === meId);

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Stats d'équipe</h1>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="flex gap-1 bg-raise rounded-lg p-0.5">
            {([['all', 'Tous'], ['cold_call', 'Cold call'], ['gtm', 'GTM']] as const).map(([k, lbl]) =>
              <button key={k} onClick={() => setTeam(k)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md ${team === k ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{lbl}</button>)}
          </div>
          <div className="flex gap-1 bg-raise rounded-lg p-0.5">
            {(['jour', 'semaine', 'mois'] as const).map(m => <button key={m} onClick={() => setMode(m)} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md capitalize ${mode === m ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{m}</button>)}
          </div>
          <button onClick={() => shift(-1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">‹</button>
          <button onClick={() => shift(1)} className="w-7 h-7 rounded-md border border-line-2 text-dim hover:border-ink">›</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4 capitalize">
        {range.label} · classement de l'équipe. Trié par <b>{COLS.find(c => c.key === sortKey)?.label ?? 'honorés'}</b> — clique une colonne pour changer.
        {myRank >= 0 && <span className="not-italic"> Toi : <b className="text-hook-rose">#{myRank + 1}</b>/{sorted.length}.</span>}
      </p>

      {err && <EmptyState tone="error" title="Chargement impossible" hint={err} className="mb-4" />}
      {loading ? <SkeletonRows count={7} /> : sorted.length === 0 ? <EmptyState title="Aucune donnée" hint="Change de période." /> : (
        <div className="bg-panel border border-line rounded-card overflow-x-auto">
          <table className="w-full text-[12.5px] border-collapse min-w-[720px]">
            <thead>
              <tr className="border-b border-line">
                <th className="text-left font-mono text-[9px] uppercase tracking-widest text-faint px-3 py-2 w-8">#</th>
                <th className="text-left font-mono text-[9px] uppercase tracking-widest text-faint px-3 py-2">SDR</th>
                {COLS.map((c, i) => (
                  <th key={c.key} title={c.hint}
                    className={`text-right px-3 py-2 cursor-pointer select-none ${c.grp === 'act' ? 'text-dim' : 'text-hook-rose'} ${COLS[i - 1]?.grp === 'act' && c.grp === 'conv' ? 'border-l border-line' : ''}`}
                    onClick={() => setSortKey(c.key)}>
                    <span className={`font-mono text-[9px] uppercase tracking-widest ${sortKey === c.key ? 'underline' : ''}`}>{c.label}{sortKey === c.key ? ' ▾' : ''}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r, i) => {
                const me = r.sdrId === meId;
                return (
                  <tr key={r.sdrId} className={`border-b border-line last:border-0 ${me ? 'bg-hook-rose/5' : 'hover:bg-panel-2'}`}>
                    <td className="px-3 py-2 font-mono text-faint tabular-nums">{i + 1}</td>
                    <td className="px-3 py-2">
                      <span className="font-semibold">{r.name}</span>{me && <span className="ml-1.5 font-mono text-[8.5px] bg-hook-rose text-white px-1 py-0.5 rounded uppercase">moi</span>}
                      <span className={`ml-1.5 font-mono text-[8.5px] px-1 py-0.5 rounded uppercase ${r.type === 'gtm' ? 'bg-st-ok/10 text-st-ok' : 'bg-hook-rose/10 text-hook-rose'}`}>{r.type === 'gtm' ? 'GTM' : 'CC'}</span>
                    </td>
                    {COLS.map((c, ci) => {
                      const drillable = (c.key === 'bookes' || c.key === 'honores') && (r[c.key] as number) > 0;
                      return (
                        <td key={c.key} className={`px-3 py-2 text-right tabular-nums font-mono ${sortKey === c.key ? 'font-bold text-ink' : 'text-dim'} ${COLS[ci - 1]?.grp === 'act' && c.grp === 'conv' ? 'border-l border-line' : ''}`}>
                          {drillable
                            ? <button onClick={() => openDrill(r)} className="underline decoration-dotted underline-offset-2 hover:text-hook-rose" title="Voir les RDV">{r[c.key] as number}</button>
                            : <>{r[c.key] as number}{c.suffix || ''}</>}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="font-mono text-[9.5px] text-faint mt-2">
        <span className="text-dim">Composé</span> = numéro appelé · <span className="text-dim">Connecté</span> = décroché · <span className="text-dim">Conversation</span> = connecté &gt; 30 s (l'unité qui compte). Chiffres d'appels = Minari (mesuré, exact). Colonnes <span className="text-hook-rose">roses = conversion</span>. Clique un nb de <b>Bookés</b>/<b>Honorés</b> pour voir les RDV.
      </p>

      {/* Drill-down : les RDV d'un SDR sur la période (retour Romain). */}
      {drill && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setDrill(null)}>
          <div className="bg-panel border border-line rounded-card p-5 w-full max-w-[520px] max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[14px] font-bold">RDV de {drill.name} · {range.label}</span>
              <button onClick={() => setDrill(null)} aria-label="Fermer" className="text-faint hover:text-ink text-[18px] leading-none">×</button>
            </div>
            {drill.loading ? <SkeletonRows count={4} /> : drill.rows.length === 0 ? <EmptyState title="Aucun RDV" hint="Sur cette période." /> : (
              <div className="divide-y divide-line">
                {drill.rows.map(r => (
                  <div key={r.id} className="py-2 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] text-faint w-20 shrink-0">{r.date}</span>
                    <span className="text-[12.5px] font-semibold truncate flex-1 min-w-0">{r.prospect_name || 'Prospect'}{r.company_name && <span className="text-dim font-normal"> · {r.company_name}</span>}</span>
                    <span className="font-mono text-[10px] text-faint">{r.clients?.name || '—'}</span>
                    <StatusBadge status={r.lifecycle_status ?? null} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsEquipePage;
